/**
 * Inspire-hub 2 (www.giap.pl)
 *
 * Copyright © GIAP sp.z.o.o. All rights reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Krzysztof Gregorowicz <gregorowicz.k@gmail.com>, February 2017
 */

import React, { Children, PropTypes } from 'react';
import { IntlProvider } from 'react-intl';
import { propertiesMap, propertiesPattern, layersMap, layersPattern } from '../data/propertiesMap';

const ContextType = {
  // Enables critical path CSS rendering
  // https://github.com/kriasoft/isomorphic-style-loader
  insertCss: PropTypes.func.isRequired,
  // Integrate Redux
  // http://redux.js.org/docs/basics/UsageWithReact.html
  store: PropTypes.shape({
    subscribe: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
    getState: PropTypes.func.isRequired,
  }).isRequired,
};

String.prototype.normalizeLayerName = function (layer) {
  return _.upperFirst(_.replace(layer, new RegExp('_', 'g'), ' '));
};
String.prototype.normalizePropertiesNames = function (property) {
  return property
    .replace(/_/g, ' ')
    .replace(propertiesPattern, matched => propertiesMap[matched.toLowerCase()])
    .replace(/[^%]*/i, matched => matched[0].toUpperCase() + matched.slice(1))
    .replace(/([a-z])([A-Z])/g, '$1 $2');
};
String.prototype.normalizeLayersNames = function (layer) {
  for (let i of Object.keys(layersMap)) {
    if (layer === i) return layersMap[i].replace(/[^%]*/i, matched => matched[0].toUpperCase() + matched.slice(1));
  }
  return layer;
};

// Guide:
// .replace(/_/g, ' ')  // match every _ character and replace with a space
// .replace(propertiesPattern, matched => propertiesMap[matched]) // if matched, changes key to value from object propertiesMap
// .replace(/[^%]*/i)[0], matched => matched[0].toUpperCase() + matched.slice(1)) // match zero-or-more character, which is not a % - and to upper case
// .replace(/([a-z])([A-Z])/g, '$1 $2'); // divides camelCaseWords like: AD JednostkaAdministracyjna into AD Jednostka Administracyjna


/**
 * The top-level React component setting context (global) variables
 * that can be accessed from all the child components.
 *
 * https://facebook.github.io/react/docs/context.html
 *
 * Usage example:
 *
 *   const context = {
 *     history: createBrowserHistory(),
 *     store: createStore(),
 *   };
 *
 *   ReactDOM.render(
 *     <App context={context}>
 *       <Layout>
 *         <LandingPage />
 *       </Layout>
 *     </App>,
 *     container,
 *   );
 */
class App extends React.PureComponent {

  static propTypes = {
    context: PropTypes.shape(ContextType).isRequired,
    children: PropTypes.element.isRequired,
  };

  static childContextTypes = ContextType;

  getChildContext() {
    return this.props.context;
  }

  componentDidMount() {
    const store = this.props.context && this.props.context.store;
    if (store) {
      this.unsubscribe = store.subscribe(() => {
        const state = store.getState();
        const newIntl = state.intl;
        if (this.intl !== newIntl) {
          this.intl = newIntl;
          this.forceUpdate();
        }
      });
    }
  }

  componentWillUnmount() {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }

  render() {
    // NOTE: If you need to add or modify header, footer etc. of the app,
    // please do that inside the Layout component.
    const store = this.props.context && this.props.context.store;
    const state = store && store.getState();
    this.intl = (state && state.intl) || {};
    const { initialNow, locale, messages } = this.intl;
    const localeMessages = (messages && messages[locale]) || {};
    return (
      <IntlProvider
        initialNow={initialNow}
        locale={locale}
        messages={localeMessages}
        defaultLocale="pl-PL"
      >
        {Children.only(this.props.children)}
      </IntlProvider>
    );
  }

}

export default App;



// WEBPACK FOOTER //
// src/components/App.js